import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import CheckIcon from "@mui/icons-material/Check";
import SendIcon from "@mui/icons-material/Send";
import LoadingButton from "@mui/lab/LoadingButton";
import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DangerousIcon from "@mui/icons-material/Dangerous";
import IconButton from "@mui/material/IconButton";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 430,
  bgcolor: "background.paper",
  border: "2px solid #E2E8F0",
  borderRadius: "15px",
  boxShadow: 24,
  p: 4,
};

// Allowlist of regulation types that block publishing — kept in sync with
// the backend BLOCKING_REGULATION_TYPES in helpers_publish_validation.py.
// Limited to rules with class-action / agency-enforcement exposure.
export const BLOCKING_REGULATION_TYPES = [
  "meal_break",
  "meal_break_timing",
  "rest_break",
  "shift_separation",
  "minor_work_hours_14_15",
  "minor_work_hours_16_17",
];

const KNOWN_REGULATION_TYPES = [
  "weekly_overtime",
  "daily_overtime",
  "seventh_day_overtime",
  "shift_separation",
  "meal_break",
  "meal_break_timing",
  "rest_break",
  "rest_break_timing",
  "advance_schedule_notice",
  "schedule_change_penalty",
  "minor_work_hours_14_15",
  "minor_work_hours_16_17",
  "split_shift",
];

// Display order for bullet lists across both blocking and advisory panels.
const VIOLATION_DISPLAY_ORDER = [
  "weekly_overtime",
  "daily_overtime",
  "seventh_day_overtime",
  "shift_separation",
  "meal_break",
  "meal_break_timing",
  "rest_break",
  "rest_break_timing",
  "advance_schedule_notice",
  "schedule_change_penalty",
  "minor_work_hours",
  "split_shift",
  "other",
];

function formatViolationCount(count, type) {
  if (count <= 0) return "";
  switch (type) {
    case "weekly_overtime":
      return `${count} ${count === 1 ? "person is" : "people are"} in overtime`;
    case "daily_overtime":
      return `${count} daily overtime ${count === 1 ? "violation" : "violations"}`;
    case "seventh_day_overtime":
      return `${count} 7th day ${count === 1 ? "violation" : "violations"}`;
    case "shift_separation":
      return `${count} ${count === 1 ? "clopen" : "clopens"}`;
    case "meal_break":
      return `${count} meal break ${count === 1 ? "violation" : "violations"}`;
    case "meal_break_timing":
      return `${count} meal break timing ${count === 1 ? "violation" : "violations"}`;
    case "rest_break":
      return `${count} rest break ${count === 1 ? "violation" : "violations"}`;
    case "rest_break_timing":
      return `${count} rest break timing ${count === 1 ? "violation" : "violations"}`;
    case "advance_schedule_notice":
      return `${count} advance notice ${count === 1 ? "violation" : "violations"}`;
    case "schedule_change_penalty":
      return `${count} schedule change ${count === 1 ? "violation" : "violations"}`;
    case "minor_work_hours":
      return `${count} minor work hour ${count === 1 ? "violation" : "violations"}`;
    case "split_shift":
      return `${count} split shift ${count === 1 ? "violation" : "violations"}`;
    case "other":
      return `${count} ${count === 1 ? "other violation" : "other violations"}`;
    default:
      return "";
  }
}

function buildLaborViolationItems(violations) {
  const counts = {};
  for (const v of violations) {
    let key = v.regulation_type;
    if (key === "minor_work_hours_14_15" || key === "minor_work_hours_16_17") {
      key = "minor_work_hours";
    } else if (!KNOWN_REGULATION_TYPES.includes(v.regulation_type)) {
      key = "other";
    }
    counts[key] = (counts[key] || 0) + 1;
  }
  return VIOLATION_DISPLAY_ORDER.map((t) =>
    formatViolationCount(counts[t] || 0, t)
  ).filter(Boolean);
}

function PublishScheduleButton(props) {

  const scheduleNeedsReview =
    props.publishScheduleReviewRequiredEnabled && !props.canPublishSchedule;

  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    props.setPublishShiftsFinished(false);
    setButtonPressed(false);
  };
  const [buttonPressed, setButtonPressed] = React.useState(false);

  const handleStart = () => {
    if (props.roleType?.length === props.roleTypeOptions?.length) {
      props.publishShifts();
    } else {
      props.publishShifts(props.roleType?.join(","));
    }
    setButtonPressed(true);
  };


  useEffect(() => {
    if (props.publishShiftsFinished) {
      handleClose();
    }
  }, [props.publishShiftsFinished]);

  const tooltipTitle = scheduleNeedsReview
    ? "Submit schedule for review"
    : props.has7ShiftsIntegration
    ? "Send to 7Shifts"
    : "Publish Schedule";

  const modalDescription = scheduleNeedsReview
    ? "We'll notify a schedule publisher so they can review and publish this schedule."
    : props.has7ShiftsIntegration
    ? "Replace existing shifts in 7shifts with the current schedule."
    : "Notifies your team of their new shifts.";


  const availabilityViolationsNum = props.countOfWarnings?.countOfConflicts || 0;
  const overtimeViolationsNum = props.laborLawEnabled ? props.laborLawViolations?.filter(x => x.regulation_type === "weekly_overtime")?.length || 0 : props.countOfWarnings?.countOfOvertime || 0;
  const dailyOvertimeViolationsNum = props.laborLawEnabled ? props.laborLawViolations?.filter(x => x.regulation_type === "daily_overtime")?.length || 0 : 0;
  const seventhDayViolationsNum = props.laborLawEnabled ? props.laborLawViolations?.filter(x => x.regulation_type === "seventh_day_overtime")?.length || 0 : 0;
  const clopenViolationsNum = props.laborLawEnabled ? props.laborLawViolations?.filter(x => x.regulation_type === "shift_separation")?.length || 0 : 0;
  const mealBreakViolationsNum = props.laborLawEnabled ? props.laborLawViolations?.filter(x => x.regulation_type === "meal_break")?.length || 0 : 0;
  const mealBreakTimingViolationsNum = props.laborLawEnabled ? props.laborLawViolations?.filter(x => x.regulation_type === "meal_break_timing")?.length || 0 : 0;
  const restBreakViolationsNum = props.laborLawEnabled ? props.laborLawViolations?.filter(x => x.regulation_type === "rest_break")?.length || 0 : 0;
  const restBreakTimingViolationsNum = props.laborLawEnabled ? props.laborLawViolations?.filter(x => x.regulation_type === "rest_break_timing")?.length || 0 : 0;
  const advanceNoticeViolationsNum = props.laborLawEnabled ? props.laborLawViolations?.filter(x => x.regulation_type === "advance_schedule_notice")?.length || 0 : 0;
  const scheduleChangePenaltyNum = props.laborLawEnabled ? props.laborLawViolations?.filter(x => x.regulation_type === "schedule_change_penalty")?.length || 0 : 0;
  const minorWorkHoursNum = props.laborLawEnabled ? props.laborLawViolations?.filter(x => x.regulation_type === "minor_work_hours_14_15" || x.regulation_type === "minor_work_hours_16_17")?.length || 0 : 0;
  const splitShiftViolationsNum = props.laborLawEnabled ? props.laborLawViolations?.filter(x => x.regulation_type === "split_shift")?.length || 0 : 0;
  const knownTypes = ["weekly_overtime", "daily_overtime", "seventh_day_overtime", "shift_separation", "meal_break", "meal_break_timing", "rest_break", "rest_break_timing", "advance_schedule_notice", "schedule_change_penalty", "minor_work_hours_14_15", "minor_work_hours_16_17", "split_shift"]
  const otherViolationsNum = props.laborLawEnabled ? props.laborLawViolations?.filter(x => !knownTypes.includes(x.regulation_type))?.length || 0 : 0;

  const totalWarnings = availabilityViolationsNum + overtimeViolationsNum + dailyOvertimeViolationsNum + seventhDayViolationsNum + clopenViolationsNum + mealBreakViolationsNum + mealBreakTimingViolationsNum + restBreakViolationsNum + restBreakTimingViolationsNum + advanceNoticeViolationsNum + scheduleChangePenaltyNum + minorWorkHoursNum + splitShiftViolationsNum + otherViolationsNum

  const blockingViolationsCount = props.laborLawEnabled
    ? (props.laborLawViolations || []).filter(
        (v) =>
          v.severity === "ERROR" &&
          BLOCKING_REGULATION_TYPES.includes(v.regulation_type)
      ).length
    : 0;
  // Partial publishes pass a role_type_filter that narrows what the backend
  // evaluates, but violation.shifts is not a reliable signal for which
  // role_types contributed (e.g. weekly_overtime emits a single
  // representative shift). Rather than approximate client-side, we only gate
  // proactively for full publishes; partial publishes are evaluated by the
  // backend and surface as a toast on 422 rejection.
  const isFullPublish =
    !props.roleType ||
    !props.roleTypeOptions ||
    props.roleType.length === props.roleTypeOptions.length;
  const isBlocked =
    !!props.blockPublishOnLaborViolationsEnabled &&
    isFullPublish &&
    blockingViolationsCount > 0;
  const blockedActionLabel = scheduleNeedsReview ? "submitted for review" : "published";
  // Outer-button tooltip when proactively blocked. Overrides the normal labels.
  const blockedTooltipTitle = `${blockingViolationsCount} labor law ${
    blockingViolationsCount === 1 ? "violation" : "violations"
  } must be resolved before this schedule can be ${blockedActionLabel}.`;

  // When blocked, build separate bullet lists for the red blocking panel
  // and the amber advisory panel (availability conflicts, lower-severity
  // labor law, and excluded predictability-pay rules).
  const blockingItemsText = isBlocked
    ? buildLaborViolationItems(
        (props.laborLawViolations || []).filter(
          (v) =>
            v.severity === "ERROR" &&
            BLOCKING_REGULATION_TYPES.includes(v.regulation_type)
        )
      )
    : [];
  const advisoryItemsText = isBlocked
    ? [
        ...(availabilityViolationsNum > 0
          ? [
              `${availabilityViolationsNum} ${
                availabilityViolationsNum === 1 ? "person has" : "shifts with"
              } an availability conflict`,
            ]
          : []),
        ...buildLaborViolationItems(
          (props.laborLawViolations || []).filter(
            (v) =>
              v.severity !== "ERROR" ||
              !BLOCKING_REGULATION_TYPES.includes(v.regulation_type)
          )
        ),
      ]
    : [];

  function createWarningsText(overtimeViolationsNum, dailyOvertimeViolationsNum, seventhDayViolationsNum, availabilityViolationsNum, clopenViolationsNum, mealBreakViolationsNum, mealBreakTimingViolationsNum, restBreakViolationsNum, restBreakTimingViolationsNum, advanceNoticeViolationsNum, scheduleChangePenaltyNum, minorWorkHoursNum, splitShiftViolationsNum, otherViolationsNum) {
    let overtimeViolationsText = "";
    if (overtimeViolationsNum > 0) {
      overtimeViolationsText += `${overtimeViolationsNum} ${
        overtimeViolationsNum === 1 ? "person is" : "people are"
      } in overtime`;
    }

    let dailyOvertimeViolationsText = "";
    if (dailyOvertimeViolationsNum > 0) {
      dailyOvertimeViolationsText += `${dailyOvertimeViolationsNum} daily overtime ${
        dailyOvertimeViolationsNum === 1 ? "violation" : "violations"
      }`;
    }

    let seventhDayViolationsText = "";
    if (seventhDayViolationsNum > 0) {
      seventhDayViolationsText += `${seventhDayViolationsNum} 7th day ${
        seventhDayViolationsNum === 1 ? "violation" : "violations"
      }`;
    }

    let availabilityViolationsText = "";
    if (availabilityViolationsNum > 0) {
      availabilityViolationsText += `${availabilityViolationsNum} ${
        availabilityViolationsNum === 1 ? "person has" : "shifts with"
      } an availability conflict`;
    }

    let clopenViolationsText = "";
    if (clopenViolationsNum > 0) {
      clopenViolationsText += `${clopenViolationsNum} ${
        clopenViolationsNum === 1 ? "clopen" : "clopens"
      }`;
    }

    let mealBreakViolationsText = "";
    if (mealBreakViolationsNum > 0) {
      mealBreakViolationsText += `${mealBreakViolationsNum} meal break ${
        mealBreakViolationsNum === 1 ? "violation" : "violations"
      }`;
    }

    let mealBreakTimingViolationsText = "";
    if (mealBreakTimingViolationsNum > 0) {
      mealBreakTimingViolationsText += `${mealBreakTimingViolationsNum} meal break timing ${
        mealBreakTimingViolationsNum === 1 ? "violation" : "violations"
      }`;
    }

    let restBreakViolationsText = "";
    if (restBreakViolationsNum > 0) {
      restBreakViolationsText += `${restBreakViolationsNum} rest break ${
        restBreakViolationsNum === 1 ? "violation" : "violations"
      }`;
    }

    let restBreakTimingViolationsText = "";
    if (restBreakTimingViolationsNum > 0) {
      restBreakTimingViolationsText += `${restBreakTimingViolationsNum} rest break timing ${
        restBreakTimingViolationsNum === 1 ? "violation" : "violations"
      }`;
    }

    let advanceNoticeViolationText = "";
    if (advanceNoticeViolationsNum > 0) {
      advanceNoticeViolationText = `${advanceNoticeViolationsNum} advance notice ${
        advanceNoticeViolationsNum === 1 ? "violation" : "violations"
      }`;
    }

    let scheduleChangePenaltyText = "";
    if (scheduleChangePenaltyNum > 0) {
      scheduleChangePenaltyText = `${scheduleChangePenaltyNum} schedule change ${
        scheduleChangePenaltyNum === 1 ? "violation" : "violations"
      }`;
    }

    let minorWorkHoursText = "";
    if (minorWorkHoursNum > 0) {
      minorWorkHoursText = `${minorWorkHoursNum} minor work hour ${
        minorWorkHoursNum === 1 ? "violation" : "violations"
      }`;
    }

    let splitShiftViolationsText = "";
    if (splitShiftViolationsNum > 0) {
      splitShiftViolationsText = `${splitShiftViolationsNum} split shift ${
        splitShiftViolationsNum === 1 ? "violation" : "violations"
      }`;
    }

    let otherViolationsText = "";
    if (otherViolationsNum > 0) {
      otherViolationsText += `${otherViolationsNum} ${
        otherViolationsNum === 1 ? "other violation" : "other violations"
      }`;
    }

    return [overtimeViolationsText, dailyOvertimeViolationsText, seventhDayViolationsText, availabilityViolationsText, clopenViolationsText, mealBreakViolationsText, mealBreakTimingViolationsText, restBreakViolationsText, restBreakTimingViolationsText, advanceNoticeViolationText, scheduleChangePenaltyText, minorWorkHoursText, splitShiftViolationsText, otherViolationsText];
  }

  const warningsText = createWarningsText(
    overtimeViolationsNum, dailyOvertimeViolationsNum, seventhDayViolationsNum, availabilityViolationsNum, clopenViolationsNum, mealBreakViolationsNum, mealBreakTimingViolationsNum, restBreakViolationsNum, restBreakTimingViolationsNum, advanceNoticeViolationsNum, scheduleChangePenaltyNum, minorWorkHoursNum, splitShiftViolationsNum, otherViolationsNum
  );

  // This text will be displayed on the button
  let publishButtonText;
  if (props.countOfUnpublished === 0) {
    publishButtonText = "Published";
  } else if (props.monthlyView && props.monthName) {
    publishButtonText = scheduleNeedsReview
      ? "Submit Schedule for Review"
      : `Publish ${props.monthName}`;
  } else if (scheduleNeedsReview) {
    publishButtonText = props.countOfPublished > 0
      ? "Submit Changes for Review"
      : "Submit Schedule for Review";
  } else {
    const n = props.countOfUnpublished;
    publishButtonText = props.countOfPublished > 0
      ? `Publish ${n} ${n === 1 ? "change" : "changes"}`
      : "Publish schedule";
  }

  // You can also disable the button if there are no changes to publish
  // Two separate things. Nothing to publish is a state the button reports:
  // it goes quiet and reads "All published". Being blocked from publishing is
  // not, and folding it in here told managers their pending edits were already
  // out, which was worse than leaving the button live.
  const nothingToPublish = props.countOfUnpublished === 0;
  const isButtonDisabled = nothingToPublish || !!props.disabled;

  return (
    <div>
      <Tooltip
        title={
          props.disabled && props.disabledReason
            ? props.disabledReason
            : props.countOfUnpublished === 0
            ? "No changes to publish"
            : isBlocked
            ? blockedTooltipTitle
            : tooltipTitle
        }
      >
        <span>
          <Button
            data-help-id="schedule-publish-button"
            variant="contained"
            disableElevation
            endIcon={nothingToPublish ? <CheckIcon /> : <SendIcon />}
            onClick={handleOpen}
            disabled={isButtonDisabled}
            sx={{
              backgroundColor: "#6d28d9",
              "&:hover": {
                backgroundColor: "#5b21b6",
              },
              "&.Mui-disabled": {
                backgroundColor: "#f1f5f9",
                color: "#64748b",
              },
              textTransform: "none",
              borderRadius: "8px",
              // Matches the issues pill beside it, so the two read as one row.
              height: 36,
              py: 0,
            }}
          >
            {nothingToPublish
              ? "All published"
              : props.windowWidth <= 1279
              ? "Publish"
              : publishButtonText}
          </Button>
        </span>
      </Tooltip>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          {scheduleNeedsReview ? (
            <div className="pt-2">
              <div className="absolute right-2 top-2">
                <IconButton
                  className="text-gray-400 hover:text-gray-600"
                  onClick={handleClose}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>

              <div className="pb-6 mb-6 border-b border-gray-200">
                <div className="flex items-center mb-2">
                  <DangerousIcon className="text-rose-600 mr-3" />
                  <h2 className="text-xl font-semibold text-gray-900">
                    Submit Schedule for Review
                  </h2>
                </div>
                <p className="text-gray-600 text-sm">
                  Only teammates with the <span className="font-medium">Schedule Publisher </span>
                  access can publish schedules for this store. Submitting for
                  review will notify them that this schedule is ready so they
                  can review and publish it on your behalf.
                </p>
              </div>

              {isBlocked ? (
                <>
                  <div className="mb-4">
                    <div className="p-4 bg-rose-50 border border-rose-200 rounded-lg">
                      <div className="flex items-start space-x-3">
                        <DangerousIcon className="text-rose-600 h-5 w-5 mt-0.5" />
                        <div>
                          <p className="text-rose-700 text-sm font-medium mb-2">
                            {`${blockingViolationsCount} labor law ${
                              blockingViolationsCount === 1 ? "violation" : "violations"
                            } must be resolved before this schedule can be ${blockedActionLabel}.`}
                          </p>
                          <ul className="space-y-1">
                            {blockingItemsText.map((text, i) => (
                              <li key={i} className="text-rose-700 text-sm flex items-center space-x-2">
                                <span className="text-xs">•</span>
                                <span>{text}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  {advisoryItemsText.length > 0 && (
                    <div className="mb-6">
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start space-x-3">
                          <WarningIcon className="text-amber-600 h-5 w-5 mt-0.5" />
                          <div>
                            <p className="text-amber-800 text-sm font-medium mb-1">
                              Other schedule issues
                            </p>
                            <p className="text-amber-700 text-xs mb-2">
                              These don't block submitting for review but are worth reviewing.
                            </p>
                            <ul className="space-y-1">
                              {advisoryItemsText.map((text, i) => (
                                <li key={i} className="text-amber-800 text-sm flex items-center space-x-2">
                                  <span className="text-xs">•</span>
                                  <span>{text}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                totalWarnings > 0 && (
                  <div className="mb-6">
                    <p className="text-gray-500 text-xs mb-2">Other schedule issues</p>
                    <div className="p-4 bg-rose-50 border border-rose-200 rounded-lg">
                      <div className="flex items-start space-x-3">
                        <WarningIcon className="text-rose-600 h-5 w-5 mt-0.5" />
                        <div>
                          <p className="text-rose-700 text-sm font-medium mb-2">
                            {`This schedule has ${totalWarnings} ${
                              totalWarnings === 1 ? "issue" : "issues"
                            }. Are you sure you want to publish this schedule?`}
                          </p>
                          <ul className="space-y-1">
                            {warningsText?.map((text, index) => (
                              text?.length > 0 && (
                                <li key={index} className="text-rose-700 text-sm flex items-center space-x-2">
                                  <span className="text-xs">•</span>
                                  <span>{text}</span>
                                </li>
                              )
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              )}

              <div className="flex items-center space-x-3">
                <button
                  onClick={handleClose}
                  className="px-6 py-2.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors duration-200 text-sm font-medium"
                >
                  Cancel
                </button>
                <LoadingButton
                  fullWidth
                  variant="contained"
                  disabled={isBlocked}
                  loading={buttonPressed}
                  loadingPosition="end"
                  endIcon={<SendIcon />}
                  onClick={() => handleStart()}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    padding: "10px 16px",
                    backgroundColor: "#6d28d9",
                    "&:hover": {
                      backgroundColor: "#5b21b6",
                    },
                  }}
                >
                  Submit for Review
                </LoadingButton>
              </div>
            </div>
          ) : (
            <div className="pt-2">
              <div className="absolute right-2 top-2">
                <IconButton
                  className="text-gray-400 hover:text-gray-600"
                  onClick={handleClose}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>

              <div className="pb-6 mb-6 border-b border-gray-200">
                <div className="flex items-center mb-2">
                  <SendIcon className="text-violet-600 mr-3" />
                  <h2 className="text-xl font-semibold text-gray-900">
                    {props.monthlyView && props.monthName
                      ? `Publish ${props.monthName}`
                      : props.countOfPublished > 0
                      ? `Publish ${props.countOfUnpublished} ${
                          props.countOfUnpublished === 1 ? "Change" : "Changes"
                        }`
                      : `Publish Schedule`}
                  </h2>
                </div>
                <p className="text-gray-600 text-sm">
                  {props.monthlyView && props.monthName
                    ? `You will be publishing the ${props.monthName} schedule to ${
                        props.roleType?.length === props.roleTypeOptions?.length
                          ? "all departments"
                          : props.roleType
                              .map((department, index) =>
                                index > 0
                                  ? ` and your ${department} department`
                                  : ` your ${department} department`
                              )
                              .join("")
                      }.`
                    : `You will be publishing ${
                        props.countOfPublished > 0 ? "these changes" : "this schedule"
                      } to ${
                        props.roleType?.length === props.roleTypeOptions?.length
                          ? "all departments"
                          : props.roleType
                              .map((department, index) =>
                                index > 0
                                  ? ` and your ${department} department`
                                  : ` your ${department} department`
                              )
                              .join("")
                      }.`}
                </p>
              </div>

              {!isBlocked && (
                <div className="mb-6">
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center space-x-3">
                    <CheckCircleIcon className="text-emerald-600 h-5 w-5" />
                    <p className="text-emerald-700 text-sm font-medium">
                      {modalDescription}
                    </p>
                  </div>
                </div>
              )}

              {isBlocked ? (
                <>
                  <div className="mb-4">
                    <div className="p-4 bg-rose-50 border border-rose-200 rounded-lg">
                      <div className="flex items-start space-x-3">
                        <DangerousIcon className="text-rose-600 h-5 w-5 mt-0.5" />
                        <div>
                          <p className="text-rose-700 text-sm font-medium mb-2">
                            {`${blockingViolationsCount} labor law ${
                              blockingViolationsCount === 1 ? "violation" : "violations"
                            } must be resolved before this schedule can be ${blockedActionLabel}.`}
                          </p>
                          <ul className="space-y-1">
                            {blockingItemsText.map((text, i) => (
                              <li key={i} className="text-rose-700 text-sm flex items-center space-x-2">
                                <span className="text-xs">•</span>
                                <span>{text}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  {advisoryItemsText.length > 0 && (
                    <div className="mb-6">
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start space-x-3">
                          <WarningIcon className="text-amber-600 h-5 w-5 mt-0.5" />
                          <div>
                            <p className="text-amber-800 text-sm font-medium mb-1">
                              Other schedule issues
                            </p>
                            <p className="text-amber-700 text-xs mb-2">
                              These don't block publishing but are worth reviewing.
                            </p>
                            <ul className="space-y-1">
                              {advisoryItemsText.map((text, i) => (
                                <li key={i} className="text-amber-800 text-sm flex items-center space-x-2">
                                  <span className="text-xs">•</span>
                                  <span>{text}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                !props.monthlyView && totalWarnings > 0 && (
                  <div className="mb-6">
                    <div className="p-4 bg-rose-50 border border-rose-200 rounded-lg">
                      <div className="flex items-start space-x-3">
                        <WarningIcon className="text-rose-600 h-5 w-5 mt-0.5" />
                        <div>
                          <p className="text-rose-700 text-sm font-medium mb-2">
                            {`This schedule has ${totalWarnings} ${
                              totalWarnings === 1 ? "issue" : "issues"
                            }. Are you sure you want to publish this schedule?`}
                          </p>
                          <ul className="space-y-1">
                            {warningsText?.map((text, index) => (
                              text?.length > 0 && (
                                <li key={index} className="text-rose-700 text-sm flex items-center space-x-2">
                                  <span className="text-xs">•</span>
                                  <span>{text}</span>
                                </li>
                              )
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              )}

              <div className="flex items-center space-x-3">
                <button
                  onClick={handleClose}
                  className="px-6 py-2.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors duration-200 text-sm font-medium"
                >
                  Cancel
                </button>
                <LoadingButton
                  fullWidth
                  variant="contained"
                  disabled={isBlocked}
                  loading={buttonPressed}
                  loadingPosition="end"
                  endIcon={<SendIcon />}
                  onClick={() => handleStart()}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    padding: "10px 16px",
                    backgroundColor: "#6d28d9",
                    "&:hover": {
                      backgroundColor: "#5b21b6",
                    },
                  }}
                >
                  {props.monthlyView && props.monthName
                    ? `Publish ${props.monthName}`
                    : props.countOfPublished > 0
                    ? totalWarnings > 0 && !isBlocked
                      ? "Publish Anyway"
                      : "Publish Changes"
                    : totalWarnings > 0 && !isBlocked
                    ? "Publish Anyway"
                    : "Publish Schedule"}
                </LoadingButton>
              </div>
            </div>
          )}
        </Box>
      </Modal>
    </div>
  );
}

export default PublishScheduleButton;
